import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Fragment } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

import type { BankAccount } from "@/hooks/use-bank-accounts";

import { useOpeningBalancesTable } from "./TableLogic";
import { buildReconLink, gapBg, gapRing, prettyDate, type MetricKey } from "./TableHelpers";

export function OpeningBalancesTable(props: {
  accounts: BankAccount[];
  dateFrom: string;
  dateTo: string;
  className?: string;
}) {
  const { accounts, dateFrom, dateTo, className } = props;

  const {
    metrics,
    enabledMetrics,
    toggle,
    dateKeys,
    isLoading,
    isError,
    getMapsForAccount,
    resetToGap,
    selectAll,
  } = useOpeningBalancesTable({ accounts, dateFrom, dateTo });


  const openingEnabled = metrics.opening;
  const expectedEnabled = metrics.expected;
  const ledgerExpectedEnabled = metrics.ledgerExpected;
  const gapEnabled = metrics.gap;

  return (
    <Card className={cn("border-none shadow-lg overflow-hidden bg-white", className)}>
      <CardHeader className="bg-slate-900 border-none pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-white text-base">Cash Flow Details Matrix</CardTitle>
            <CardDescription className="text-slate-400 text-[10px]">
              Choose metrics to display (loads only selected)
            </CardDescription>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <button
                type="button"
                onClick={resetToGap}
                className={cn(
                  "h-7 px-2 rounded border border-slate-700 bg-slate-800",
                  "text-[11px] font-semibold text-slate-200 hover:bg-slate-700"
                )}
              >
                Reset
              </button>

              <button
                type="button"
                onClick={selectAll}
                className={cn(
                  "h-7 px-2 rounded border border-slate-700 bg-slate-800",
                  "text-[11px] font-semibold text-slate-200 hover:bg-slate-700"
                )}
              >
                Select all
              </button>
              <label className="flex items-center gap-2 text-[11px] text-slate-200">
                <Checkbox checked={openingEnabled} onCheckedChange={() => toggle("opening")} />
                Opening
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-200">
                <Checkbox checked={metrics.txIn} onCheckedChange={() => toggle("txIn")} />
                Tx In
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-200">
                <Checkbox checked={metrics.txOut} onCheckedChange={() => toggle("txOut")} />
                Tx Out
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-200">
                <Checkbox checked={metrics.fees} onCheckedChange={() => toggle("fees")} />
                Fees
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-200">
                <Checkbox checked={expectedEnabled} onCheckedChange={() => toggle("expected")} />
                Expected closing
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-200">
                <Checkbox checked={metrics.exp} onCheckedChange={() => toggle("exp")} />
                Expenses
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-200">
                <Checkbox checked={metrics.inflows} onCheckedChange={() => toggle("inflows")} />
                Cash Inflows
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-200">
                <Checkbox
                  checked={ledgerExpectedEnabled}
                  onCheckedChange={() => toggle("ledgerExpected")}
                />
                Ledger expected
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-200">
                <Checkbox checked={gapEnabled} disabled />
                Ledger gap
              </label>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : isError ? (
          <div className="p-4 text-sm font-semibold text-rose-700 bg-rose-50">
            Failed to load data
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-max table-auto border-separate border-spacing-y-3">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 z-20 bg-slate-50 w-72 px-4 py-3 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-slate-200">
                    Account
                  </th>
                  {dateKeys.map((d) => (
                    <th
                      key={d}
                      className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[140px]"
                    >
                      {prettyDate(d)}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {accounts.map((acc) => {
                  const rowCcyFallback = (acc.currency ?? "ETB") as any;
                  const maps = getMapsForAccount(acc.id);

                  return (
                    <Fragment key={acc.id}>
                      {enabledMetrics.map((m, idx) => {
                        const isPrimaryRow = idx === 0;

                        return (
                          <tr
                            key={m.key}
                            className={cn(m.rowClass, isPrimaryRow && "border-t-8 border-transparent")}
                          >
                            <td
                              className={cn(
                                "sticky left-0 z-10 px-4 py-2 border-r border-slate-100 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]",
                                m.leftBg
                              )}
                            >
                              {isPrimaryRow ? (
                                <div className="flex items-baseline justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="text-sm font-bold text-slate-900 truncate">
                                      {acc.name}
                                    </div>
                                    <div className="text-xs text-slate-500 truncate">
                                      {acc.currency ?? ""}
                                    </div>
                                  </div>
                                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 shrink-0">
                                    {m.pill}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <div className="text-[10px] text-slate-500 font-semibold">
                                    {m.label}
                                  </div>
                                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                    {m.pill}
                                  </div>
                                </div>
                              )}
                            </td>

                            {dateKeys.map((d) => {
                              const cell = m.getCell({ dateKey: d, rowCcyFallback, maps });

                              return (
                                <td
                                  key={d}
                                  title={cell.title ?? ""}
                                  className={cn(
                                    "px-4 py-2 text-right tabular-nums text-[11px] leading-none whitespace-nowrap",
                                    m.key === "gap" &&
                                    cn("rounded-md transition-colors", gapBg(cell.val), gapRing(cell.val)),
                                    cell.className
                                  )}
                                >
                                  {m.key === "gap" ? (
                                    <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                                      <span>
                                        {cell.val == null ? "—" : formatCurrency(cell.val, cell.ccy)}
                                      </span>

                                      {cell.val != null ? (
                                        <Link
                                          to={buildReconLink({ accountId: acc.id, date: d, onlyGaps: true })}
                                          target="_blank"
                                          className={cn(
                                            "inline-flex items-center justify-center h-5 w-5 shrink-0 rounded border border-slate-200 bg-white",
                                            "text-slate-600 hover:text-slate-900 hover:bg-slate-50 shadow-sm"
                                          )}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <ExternalLink className="h-3 w-3" />
                                        </Link>
                                      ) : null}
                                    </div>
                                  ) : cell.val == null ? (
                                    <span>—</span>
                                  ) : (
                                    <div className="flex flex-col items-end gap-1">
                                      <span>{formatCurrency(cell.val, cell.ccy)}</span>
                                      {cell.subText ? (
                                        <span className="text-[10px] text-slate-500 font-medium">
                                          {cell.subText}
                                        </span>
                                      ) : null}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}